import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild, computed, effect, inject } from '@angular/core';
import { CallService } from '../../../core/services/call.service';
import { NombreTono, TONO_ACTIVO, TONO_SALIENTE, TONOS, reproducirCiclo } from '../ringtone';

/**
 * Modal de llamada, montado en el shell de la aplicación (fuera del
 * router-outlet) para que suene esté el agente en la pantalla que esté.
 *
 * El timbre se sintetiza con WebAudio en vez de reproducir un mp3: evita meter
 * un binario en el repo y, sobre todo, evita el bloqueo de autoplay — un
 * oscilador arranca sin gesto previo del usuario donde un <audio> con src
 * externo se quedaría mudo hasta el primer clic.
 */
@Component({
    selector: 'app-call-modal',
    standalone: false,
    templateUrl: './call-modal.component.html',
    styleUrls: ['./call-modal.component.css'],
})
export class CallModalComponent implements AfterViewInit, OnDestroy {
    @ViewChild('audioRemoto') audioRemoto!: ElementRef<HTMLAudioElement>;

    private calls = inject(CallService);

    estado = this.calls.estado;
    llamada = this.calls.llamada;
    error = this.calls.error;
    silenciado = this.calls.silenciado;
    minimizada = this.calls.minimizada;
    calidad = this.calls.calidad;
    microfono = this.calls.microfono;

    visible = computed(() => this.estado() !== 'inactiva');

    /** Semáforo de red: color, texto y el detalle numérico para el tooltip. */
    // Sin eñe: el parser de plantillas de Angular no acepta identificadores con
    // caracteres fuera de ASCII.
    senal = computed(() => {
        const c = this.calidad();
        if (!c) return null;
        const partes = [
            c.latenciaMs !== null ? `latencia ${c.latenciaMs} ms` : null,
            c.perdidaPct !== null ? `pérdida ${c.perdidaPct}%` : null,
            c.jitterMs !== null ? `jitter ${c.jitterMs} ms` : null,
            c.porRelay ? 'vía TURN' : 'conexión directa',
        ].filter(Boolean);

        const mapa = {
            buena: { clase: 'llamada-senal--buena', texto: 'Buena' },
            regular: { clase: 'llamada-senal--regular', texto: 'Regular' },
            mala: { clase: 'llamada-senal--mala', texto: 'Mala' },
        } as const;

        return { ...mapa[c.nivel], detalle: partes.join(' · ') };
    });
    duracion = computed(() => {
        const s = this.calls.segundos();
        return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
    });

    private audioCtx: AudioContext | null = null;
    private timbre: { sonando: OscillatorNode[]; patron: any } | null = null;
    private notificacion: Notification | null = null;
    private parpadeo: any = null;
    private tituloPrevio = '';

    constructor() {
        effect(() => {
            const e = this.estado();
            // Entrante sonando: hay que reclamar la atención del agente, salvo
            // que él mismo haya apagado el timbre de esta llamada.
            if (e === 'sonando') {
                if (!this.calls.timbreApagado()) this.avisar();
                return;
            }

            this.dejarDeAvisar();

            // Saliente esperando: el agente ya está mirando la pantalla, así que
            // no se le avisa de nada — solo se le confirma que la línea sigue
            // viva con el tono de espera.
            if (e === 'llamando') this.timbrar(TONO_SALIENTE);
        });
    }

    ngAfterViewInit(): void {
        this.calls.registrarAudio(this.audioRemoto.nativeElement);
    }

    ngOnDestroy(): void {
        this.dejarDeAvisar();
        void this.audioCtx?.close().catch(() => undefined);
    }

    /**
     * Avisa por los tres canales a la vez. El modal solo se ve si el CRM está
     * en primer plano, y un agente con el navegador en otra pestaña —o con el
     * CRM detrás de Excel— se perdería la llamada entera sin enterarse.
     */
    private avisar(): void {
        this.timbrar();
        this.notificar();
        this.parpadearTitulo();
    }

    private dejarDeAvisar(): void {
        this.callar();
        this.notificacion?.close();
        this.notificacion = null;
        if (this.parpadeo) {
            clearInterval(this.parpadeo);
            this.parpadeo = null;
            document.title = this.tituloPrevio;
        }
    }

    /** Notificación del sistema: se ve aunque el navegador esté minimizado. */
    private notificar(): void {
        if (!('Notification' in window) || Notification.permission !== 'granted') return;

        const llamada = this.llamada();
        try {
            this.notificacion = new Notification('Llamada de WhatsApp', {
                body: `${llamada?.nombre || 'Número desconocido'} · +${llamada?.telefono}`,
                icon: '/icon-192x192.png',
                // Mismo tag = la notificación se reemplaza en vez de apilarse.
                tag: llamada?.wacid,
                requireInteraction: true,
            });
            this.notificacion.onclick = () => {
                window.focus();
                this.notificacion?.close();
            };
        } catch {
            // Android exige ServiceWorkerRegistration.showNotification en vez del
            // constructor. Que falle aquí no es grave: quedan el timbre y el título.
        }
    }

    /**
     * Parpadeo del título de la pestaña. Es el aviso que mejor funciona cuando
     * el agente está en otra pestaña del MISMO navegador, donde la notificación
     * del sistema a veces no se muestra.
     */
    private parpadearTitulo(): void {
        if (this.parpadeo) return;
        this.tituloPrevio = document.title;
        let alterno = false;
        this.parpadeo = setInterval(() => {
            alterno = !alterno;
            document.title = alterno ? '📞 Llamada entrante…' : this.tituloPrevio;
        }, 900);
    }

    contestar(): void {
        void this.calls.contestar();
    }

    /** Calla el timbre sin rechazar: la llamada sigue su curso. */
    silenciarTimbre(): void {
        this.calls.silenciarTimbre();
        this.callar();
    }

    minimizar(): void {
        this.calls.minimizar();
        this.callar();
    }

    restaurar(): void {
        this.calls.restaurar();
    }

    rechazar(): void {
        void this.calls.rechazar();
    }

    colgar(): void {
        void this.calls.colgar();
    }

    alternarMicrofono(): void {
        this.calls.alternarMicrofono();
    }

    // ── Timbre ──────────────────────────────────────────────────────────────

    private timbrar(cual: NombreTono = TONO_ACTIVO): void {
        if (this.timbre) return;
        try {
            this.audioCtx ??= new AudioContext();
            const ctx = this.audioCtx;
            // Si el contexto se creó antes de que el usuario interactuara, el
            // navegador lo deja suspendido y no sonaría nada.
            if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);

            const tono = TONOS[cual];
            const sonando: OscillatorNode[] = [];

            const ciclo = () => {
                // Cada ciclo estrena osciladores: son de un solo uso por diseño.
                // Se acumulan solo hasta el siguiente ciclo, cuando ya se han
                // parado solos y se descartan.
                sonando.length = 0;
                sonando.push(...reproducirCiclo(ctx, tono));
            };
            ciclo();

            this.timbre = { sonando, patron: setInterval(ciclo, tono.ciclo * 1000) };
        } catch {
            // Sin audio disponible quedan la notificación y el título; no es fatal.
        }
    }

    private callar(): void {
        if (!this.timbre) return;
        clearInterval(this.timbre.patron);
        for (const osc of this.timbre.sonando) {
            // Cortar una nota a medias: los que ya terminaron lanzan al pararlos
            // otra vez, y da igual.
            try { osc.stop(); } catch { /* ya estaba parado */ }
        }
        this.timbre = null;
    }
}
